import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Modal, Form, Input, Checkbox, message } from 'antd';
import { /*Attachment, */ Attachment, Close, List, Text1 } from 'refly-icons';
import { useTranslation } from 'react-i18next';
import type { UploadFile } from 'antd/es/upload/interface';
import { useCanvasContext } from '@refly-packages/ai-workspace-common/context/canvas';
import getClient from '@refly-packages/ai-workspace-common/requests/proxiedRequest';
import { cn, genVariableID } from '@refly/utils';
import { MAX_VARIABLE_LENGTH } from '../node-preview/start';
import { StringTypeForm } from './string-type-form';
import { ResourceTypeForm } from './resource-type-form';
import { AccountTypeForm, type Account } from './account-type-form';
import { useFileUpload } from './hooks/use-file-upload';
import { useOptionsManagement } from './hooks/use-options-management';
import { useFormData } from './hooks/use-form-data';
import { getFileType } from './utils';
import type { CreateVariablesModalProps, VariableFormData } from './types';
import type { WorkflowVariable, VariableValue } from '@refly/openapi-schema';
import { useVariableView } from '@refly-packages/ai-workspace-common/hooks/canvas';
import { useVariablesManagement } from '@refly-packages/ai-workspace-common/hooks/use-variables-management';
import './index.scss';
import { RESOURCE_TYPE } from './constants';
import { logEvent } from '@refly/telemetry-web';

interface MediaFile {
  fileId: string;
  name: string;
  type: string;
  size: number;
  storageKey?: string;
  folderId?: string;
  url?: string;
  source?: string;
  createdAt?: string;
  categoryName?: string;
  assetId?: string;
}

// 素材库条目按 assetId 引用；旧 DriveFile 选择仍按 fileId 引用。
const toLibraryResource = (file: MediaFile): NonNullable<VariableValue['resource']> =>
  file.assetId
    ? {
        name: file.name || '',
        fileType: getFileType(file.name, file.type),
        assetId: file.assetId,
        source: 'media_library',
        categoryName: file.categoryName,
      }
    : {
        name: file.name || '',
        storageKey: file.storageKey || '',
        fileType: getFileType(file.name, file.type),
        fileId: file.fileId,
        categoryName: file.categoryName,
      };

export const CreateVariablesModal: React.FC<CreateVariablesModalProps> = React.memo(
  ({
    visible,
    onCancel,
    defaultValue,
    variableType: initialVariableType,
    mode,
    disableChangeVariableType,
    isFromResource,
    showFileUploadError,
    fromToolsDependency,
  }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm<VariableFormData>();
    const [variableType, setVariableType] = useState<string>(
      defaultValue?.variableType || initialVariableType || 'string',
    );
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [selectedLibraryFiles, setSelectedLibraryFiles] = useState<MediaFile[]>([]);
    const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const { canvasId } = useCanvasContext();
    const { handleVariableView } = useVariableView(canvasId);
    const { data: workflowVariables, setVariables } = useVariablesManagement(canvasId);

    // Watch the required field to pass to ResourceTypeForm
    const isRequired = Form.useWatch('required', form) ?? true;

    const title = useMemo(() => {
      if (disableChangeVariableType) {
        return t(
          `canvas.workflow.variables.${mode === 'create' ? 'addVariableTitle' : 'editVariableTitle'}`,
          { type: t(`canvas.workflow.variables.variableTypeOptions.${variableType}`) },
        );
      }
      return t(`canvas.workflow.variables.${mode === 'create' ? 'addTitle' : 'editTitle'}`);
    }, [t, mode, disableChangeVariableType, variableType]);

    const variableTypeOptions = useMemo(() => {
      return [
        {
          label: t('canvas.workflow.variables.variableTypeOptions.string'),
          value: 'string',
          icon: <Text1 size={16} />,
        },
        {
          label: t('canvas.workflow.variables.variableTypeOptions.resource'),
          value: 'resource',
          icon: <Attachment size={16} />,
        },
        {
          label: t('canvas.workflow.variables.variableTypeOptions.option'),
          value: 'option',
          icon: <List size={16} />,
        },
      ];
    }, [t]);

    // Custom hooks
    const {
      uploading,
      handleFileUpload: uploadFile,
      handleRefreshFile: refreshFile,
    } = useFileUpload();

    const { setOptionsValue, setEditingIndex, setCurrentOption, resetOptions } =
      useOptionsManagement(defaultValue?.options || []);

    const {
      stringFormData,
      resourceFormData,
      optionFormData,
      resetFormData,
      updateStringFormData,
      updateResourceFormData,
      updateOptionFormData,
    } = useFormData();

    // Initialize form data when modal becomes visible
    useEffect(() => {
      if (visible) {
        if (defaultValue) {
          setVariableType(defaultValue.variableType || 'string');

          // Initialize form data based on variable type
          if (defaultValue.variableType === 'string') {
            const newStringFormData = {
              ...stringFormData,
              name: defaultValue.name || '',
              value: defaultValue.value || [],
              description: defaultValue.description || '',
              required: defaultValue.required ?? true,
            };
            updateStringFormData(newStringFormData);
            form.setFieldsValue(newStringFormData);
          } else if (defaultValue.variableType === 'resource') {
            const newResourceFormData = {
              ...resourceFormData,
              name: defaultValue.name || '',
              value: defaultValue.value || [],
              description: defaultValue.description || '',
              required: defaultValue.required ?? true,
              resourceTypes: defaultValue.resourceTypes || RESOURCE_TYPE,
            };
            updateResourceFormData(newResourceFormData);
            form.setFieldsValue(newResourceFormData);

            // Set file list for resource type
            const libraryValues = (defaultValue.value || []).filter(
              (value) => value.resource?.assetId,
            );
            setSelectedLibraryFiles(
              libraryValues.map((value) => ({
                fileId: value.resource!.assetId!,
                assetId: value.resource!.assetId,
                name: value.resource?.name || '',
                type: value.resource?.fileType || '',
                size: 0,
                source: 'media_library',
                categoryName: value.resource?.categoryName,
              })),
            );
            const uploadedValues = (defaultValue.value || []).filter(
              (value) => !value.resource?.assetId,
            );
            if (uploadedValues.length) {
              const files: UploadFile[] = uploadedValues.map((value) => ({
                // Use fileId as uid to track existing files
                uid: value.resource?.fileId || value.resource?.entityId || '',
                name: value.resource?.name || '',
                status: 'done',
                url: value.resource?.storageKey || '',
              }));
              setFileList(files);
            }
          } else if (defaultValue.variableType === 'option') {
            const newOptionFormData = {
              ...optionFormData,
              name: defaultValue.name || '',
              value: defaultValue.value || [],
              description: defaultValue.description || '',
              required: defaultValue.required ?? true,
              isSingle: defaultValue.isSingle ?? true,
              options: defaultValue.options || [],
            };

            updateOptionFormData(newOptionFormData);
            form.setFieldsValue(newOptionFormData);
            setOptionsValue(defaultValue.options || []);
            setCurrentOption('');

            // Restore selected accounts from stored account values
            const restoredAccounts: Account[] = (defaultValue.value || [])
              .filter((v) => v.account?.accountId)
              .map((v) => ({
                accountId: v.account!.accountId,
                userName: v.account?.userName,
                platform: v.account?.platform,
                platformType: v.account?.platformType,
                filePath: v.account?.filePath,
                categoryName: v.account?.categoryName,
              }));
            setSelectedAccounts(restoredAccounts);
          }
        }
      }
      // Note: We don't reset state here when visible becomes false
      // because it causes visual glitches during the close animation.
      // Instead, we reset state in the afterClose callback of the Modal.
    }, [visible]);

    // Form changes already update the matching local form data through
    // onValuesChange. Re-applying that state on every update creates a
    // Select -> form -> state feedback loop in the edit modal.
    // Update the form only when the active variable type changes.
    useEffect(() => {
      if (variableType === 'option') {
        form.setFieldsValue(optionFormData);
      }
      if (variableType === 'resource') {
        form.setFieldsValue(resourceFormData);
      }
      if (variableType === 'string') {
        form.setFieldsValue(stringFormData);
      }
    }, [form, variableType]);

    // Auto show value required error when from tools dependency and value is empty
    useEffect(() => {
      if (fromToolsDependency && visible && defaultValue) {
        // Check if the variable is required and has empty value
        const isRequired = defaultValue.required ?? true;
        const hasEmptyValue = !defaultValue.value || defaultValue.value.length === 0;

        if (isRequired && hasEmptyValue) {
          // Trigger form validation to show errors after a short delay to ensure form is rendered
          const timer = setTimeout(() => {
            form.validateFields().catch(() => {
              // Ignore validation errors, we just want to trigger the display
            });
          }, 300);

          return () => clearTimeout(timer);
        }
      }
    }, [fromToolsDependency, visible, defaultValue, form]);

    // Handle form values change and store in corresponding form data
    const handleFormValuesChange = useCallback(
      (_changedValues: Partial<VariableFormData>) => {
        const currentFormValues = form.getFieldsValue();
        if (variableType === 'string') {
          updateStringFormData(currentFormValues);
        } else if (variableType === 'resource') {
          updateResourceFormData(currentFormValues);
        } else if (variableType === 'option') {
          updateOptionFormData(currentFormValues);
        }
      },
      [form, variableType, updateStringFormData, updateResourceFormData, updateOptionFormData],
    );

    const handleVariableTypeChange = useCallback(
      (type: string) => {
        // Store current form data before switching
        const currentFormValues = form.getFieldsValue();

        if (variableType === 'string') {
          updateStringFormData(currentFormValues);
        } else if (variableType === 'resource') {
          updateResourceFormData(currentFormValues);
        } else if (variableType === 'option') {
          updateOptionFormData(currentFormValues);
        }

        // Switch to new type
        setVariableType(type);

        // Reset editing states
        setEditingIndex(null);
      },
      [
        form,
        variableType,
        updateStringFormData,
        updateResourceFormData,
        updateOptionFormData,
        setEditingIndex,
      ],
    );

    // Update file list and sync with resource form data
    const handleFileListChange = useCallback(
      (newFileList: UploadFile[]) => {
        setFileList(newFileList);

        // Update resource form data with current file list
        if (variableType === 'resource') {
          const uncategorized = t('canvas.workflow.variables.uncategorized') || '未分类';
          const resourceValues: VariableValue[] = newFileList.map((file) => ({
            type: 'resource',
            resource: {
              name: file.name || '',
              storageKey: file.url || '', // Use url field to store storageKey
              fileType: getFileType(file.name, file.type),
              // Newly uploaded materials default to the "Uncategorized" bucket
              categoryName: uncategorized,
            },
          }));

          updateResourceFormData({
            value: resourceValues,
          });

          // Update form values to sync with the form
          form.setFieldValue('value', resourceValues);
        }
      },
      [variableType, form, updateResourceFormData, t],
    );

    const handleFileUpload = useCallback(
      async (file: File) => {
        // Allow multiple new materials to be uploaded (0 = unlimited)
        const result = await uploadFile(file, fileList, 0);
        if (result && typeof result === 'object' && 'storageKey' in result) {
          // Add file to list with storageKey
          const newFile: UploadFile = {
            uid: result.uid,
            name: file.name,
            status: 'done',
            url: result.storageKey, // Store storageKey in url field
          };

          const newFileList = [...fileList, newFile];
          handleFileListChange(newFileList);
          return false; // Prevent default upload behavior
        }
        return false;
      },
      [fileList, handleFileListChange, uploadFile],
    );

    const handleFileRemove = useCallback(
      (file: UploadFile) => {
        const newFileList = fileList.filter((f) => f.uid !== file.uid);
        handleFileListChange(newFileList);
      },
      [fileList, handleFileListChange],
    );

    const handleRefreshFile = useCallback(() => {
      // Get old fileId from defaultValue (if editing existing variable)
      const oldFileId = defaultValue?.value?.[0]?.resource?.fileId;
      const variableId = defaultValue?.variableId || genVariableID();

      refreshFile(
        fileList,
        handleFileListChange,
        resourceFormData.resourceTypes,
        oldFileId,
        canvasId,
        variableId,
      );
    }, [
      fileList,
      handleFileListChange,
      refreshFile,
      resourceFormData.resourceTypes,
      defaultValue,
      canvasId,
    ]);

    const handleFilesSelectFromLibrary = useCallback(
      (files: MediaFile[]) => {
        setSelectedLibraryFiles(files);

        if (files.length > 0) {
          const resourceValues: VariableValue[] = files.map((file) => ({
            type: 'resource',
            resource: toLibraryResource(file),
          }));

          updateResourceFormData({
            value: resourceValues,
          });

          form.setFieldValue('value', resourceValues);
        } else {
          updateResourceFormData({
            value: [],
          });
          form.setFieldValue('value', []);
        }
      },
      [updateResourceFormData, form],
    );

    const handleAccountsSelect = useCallback(
      (accounts: Account[]) => {
        setSelectedAccounts(accounts);

        const accountValues: VariableValue[] = accounts.map((account) => ({
          type: 'text',
          // Keep text=accountId for backward compatibility with downstream consumers
          text: account.accountId,
          account: {
            accountId: account.accountId,
            userName: account.userName,
            platform: account.platform,
            platformType: account.platformType,
            filePath: account.filePath,
            categoryName: account.categoryName,
          },
        }));

        updateOptionFormData({ value: accountValues });
        form.setFieldValue('value', accountValues);
      },
      [updateOptionFormData, form],
    );

    const resetState = useCallback(() => {
      resetFormData();
      setFileList([]);
      setSelectedLibraryFiles([]);
      setSelectedAccounts([]);
      resetOptions();
      form.resetFields();
    }, [resetFormData, resetOptions, form]);

    // Handle modal close animation completion
    const handleAfterClose = useCallback(() => {
      setVariableType(initialVariableType || 'string');
      resetState();
    }, [initialVariableType, resetState]);

    const logVariableCreationEvent = useCallback(
      (variable: WorkflowVariable) => {
        try {
          if (mode !== 'create') return;
          let eventName = '';
          if (isFromResource) {
            eventName = 'create_variable_from_resource';
          } else {
            switch (variable.variableType) {
              case 'string':
                eventName = 'create_text_variable';
                break;
              case 'resource':
                eventName = 'create_resource_variable';
                break;
              case 'option':
                eventName = 'create_select_variable';
                break;
            }
          }
          logEvent(eventName, Date.now(), {
            variable,
          });
        } catch (error) {
          console.error('Error logging variable creation event:', error);
        }
      },
      [mode, isFromResource],
    );

    const saveVariable = useCallback(
      (variable: WorkflowVariable) => {
        logVariableCreationEvent(variable);
        const existingIndex = workflowVariables.findIndex(
          (v) => v.variableId === variable.variableId,
        );

        let newWorkflowVariables: WorkflowVariable[];
        if (existingIndex !== -1) {
          newWorkflowVariables = [...workflowVariables];
          newWorkflowVariables[existingIndex] = variable;
        } else {
          newWorkflowVariables = [...workflowVariables, variable];
        }

        setVariables(newWorkflowVariables);
        message.success(
          <div className="flex items-center gap-2">
            <span>
              {t('canvas.workflow.variables.saveSuccess') || 'Variable created successfully'}
            </span>
            <Button
              type="link"
              size="small"
              className="p-0 h-auto !text-refly-primary-default hover:!text-refly-primary-default"
              onClick={() => handleVariableView(variable)}
            >
              {t('canvas.workflow.variables.viewAndEdit') || 'View'}
            </Button>
          </div>,
          5, // Show for 5 seconds
        );
      },
      [t, workflowVariables, handleVariableView, logVariableCreationEvent],
    );

    const handleSubmit = useCallback(async () => {
      setSubmitting(true);
      try {
        const values = await form.validateFields();

        // Check variable count limits before creating/updating
        const currentTypeCount =
          workflowVariables?.filter(
            (v) => v.variableType === variableType && v.variableId !== defaultValue?.variableId,
          ).length ?? 0;

        if (
          currentTypeCount >= MAX_VARIABLE_LENGTH[variableType as keyof typeof MAX_VARIABLE_LENGTH]
        ) {
          const typeName =
            t(`canvas.workflow.variables.variableTypeOptions.${variableType}`) || variableType;
          message.error(
            t('canvas.workflow.variables.typeLimitReached', { type: typeName }) ||
              `${typeName} type variables have reached the maximum limit and cannot be submitted.`,
          );
          return;
        }

        // Generate variableId first (needed for DriveFile creation)
        const variableId = defaultValue?.variableId || genVariableID();

        // Construct the value array based on variable type
        let finalValue: VariableValue[];
        if (variableType === 'string') {
          const textValue = values.value?.[0]?.text?.trim() ?? '';
          finalValue = textValue ? [{ type: 'text', text: textValue }] : [];
        } else if (variableType === 'resource') {
          const uncategorized = t('canvas.workflow.variables.uncategorized') || '未分类';
          // Merge two sources: files selected from the media library, and
          // newly uploaded files (which default to the "Uncategorized" folder).
          const libraryValues: VariableValue[] = selectedLibraryFiles.map((file) => ({
            type: 'resource' as const,
            resource: toLibraryResource(file),
          }));

          // For newly uploaded files, create a DriveFile (no folderId => Uncategorized)
          const uploadedValues = await Promise.all(
            fileList.map(async (file) => {
              // Reuse existing fileId for unchanged files when editing
              const existingFileId = defaultValue?.value?.find(
                (v) => v.resource?.storageKey === file.url || v.resource?.fileId === file.uid,
              )?.resource?.fileId;

              if (existingFileId) {
                const existingResource = defaultValue?.value?.find(
                  (v) => v.resource?.fileId === existingFileId,
                )?.resource;
                return {
                  type: 'resource' as const,
                  resource: {
                    name: file.name || '',
                    fileType: getFileType(file.name, file.type),
                    fileId: existingFileId,
                    storageKey: existingResource?.storageKey || file.url || '',
                    categoryName: existingResource?.categoryName || uncategorized,
                  },
                };
              }

              // Create new DriveFile for newly uploaded files
              const storageKey = file.url || '';
              if (!storageKey) {
                throw new Error('File storage key is missing');
              }

              const { data: driveFileResponse, error } = await getClient().createDriveFile({
                body: {
                  canvasId,
                  name: file.name || '',
                  type: file.type || '',
                  storageKey,
                  source: 'variable',
                  variableId,
                  archiveFiles: true,
                },
              });

              if (error || !driveFileResponse?.data?.fileId) {
                throw new Error('Failed to create drive file');
              }

              return {
                type: 'resource' as const,
                resource: {
                  name: file.name || '',
                  fileType: getFileType(file.name, file.type),
                  fileId: driveFileResponse.data.fileId,
                  storageKey,
                  // Newly uploaded materials go to the Uncategorized bucket
                  categoryName: uncategorized,
                },
              };
            }),
          );

          // Merge and de-duplicate by fileId (fallback to storageKey)
          const merged = [...libraryValues, ...uploadedValues];
          const seen = new Set<string>();
          finalValue = merged.filter((v) => {
            const key = v.resource?.fileId || v.resource?.storageKey || '';
            if (!key) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        } else if (variableType === 'option') {
          // Account values are built from the selected accounts. Each account keeps
          // text=accountId for backward compatibility and carries its metadata
          // (including filePath) under `account` for downstream consumers.
          finalValue = selectedAccounts.map((account) => ({
            type: 'text' as const,
            text: account.accountId,
            account: {
              accountId: account.accountId,
              userName: account.userName,
              platform: account.platform,
              platformType: account.platformType,
              filePath: account.filePath,
              categoryName: account.categoryName,
            },
          }));
        } else {
          finalValue = [];
        }

        // Check if final value is empty and show error message (only for required variables)
        if (values.required && (!finalValue || finalValue.length === 0)) {
          message.error(
            t('canvas.workflow.variables.valueRequired') || 'Variable value is required',
          );
          return;
        }

        const variable: WorkflowVariable = {
          variableId,
          name: values.name,
          value: finalValue,
          description: values.description,
          variableType: variableType as 'string' | 'option' | 'resource',
          required: values.required,
          // 专用节点按 inputKind 绑定输入项，重新保存时不能丢失类型和平台限制。
          inputKind:
            variableType === 'resource'
              ? 'media'
              : variableType === 'option'
                ? 'social_account'
                : 'text',
          ...(variableType === 'option' &&
            defaultValue?.allowedPlatforms && {
              allowedPlatforms: defaultValue.allowedPlatforms,
            }),
          ...(variableType === 'resource' && {
            resourceTypes: values.resourceTypes || RESOURCE_TYPE,
            isSingle: true,
            options: [],
          }),
          ...(variableType === 'option' && {
            isSingle: false,
            options: [],
          }),
        };

        saveVariable(variable);
        onCancel(false);
      } catch (error) {
        console.error('Form validation failed:', error);
      } finally {
        setSubmitting(false);
      }
    }, [
      form,
      variableType,
      fileList,
      selectedLibraryFiles,
      selectedAccounts,
      onCancel,
      t,
      saveVariable,
      workflowVariables,
      defaultValue,
      canvasId,
    ]);

    const handleModalClose = useCallback(() => {
      onCancel(false);
    }, [onCancel]);

    const handleInputBlur = useCallback(
      (fieldName: keyof VariableFormData) => {
        const value = form.getFieldValue(fieldName);
        if (Array.isArray(value) && value.length > 0) {
          const trimmedValue = value[0]?.text?.trim();
          if (trimmedValue !== value[0]?.text) {
            form.setFieldValue(fieldName, [{ text: trimmedValue }]);
          }
        } else {
          if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (trimmedValue !== value) {
              form.setFieldValue(fieldName, trimmedValue);
            }
          }
        }
      },
      [form],
    );

    const renderForm = useCallback(() => {
      switch (variableType) {
        case 'string':
          return <StringTypeForm onBlur={() => handleInputBlur('value')} />;
        case 'resource':
          return (
            <ResourceTypeForm
              fileList={fileList}
              uploading={uploading}
              onFileUpload={handleFileUpload}
              onFileRemove={handleFileRemove}
              onRefreshFile={handleRefreshFile}
              onFilesSelect={handleFilesSelectFromLibrary}
              initialFiles={selectedLibraryFiles}
              form={form}
              showError={
                showFileUploadError && selectedLibraryFiles.length === 0 && fileList.length === 0
              }
              isRequired={isRequired}
            />
          );
        case 'option':
          return (
            <AccountTypeForm
              initialAccounts={selectedAccounts}
              onAccountsSelect={handleAccountsSelect}
              showError={showFileUploadError && selectedAccounts.length === 0}
              isRequired={isRequired}
            />
          );
        default:
          return null;
      }
    }, [
      variableType,
      fileList,
      uploading,
      handleFileUpload,
      handleFileRemove,
      handleRefreshFile,
      handleFilesSelectFromLibrary,
      handleAccountsSelect,
      showFileUploadError,
      selectedLibraryFiles,
      selectedAccounts,
      isRequired,
      form,
    ]);

    return (
      <Modal
        className="create-variables-modal"
        centered
        open={visible}
        onCancel={handleModalClose}
        afterClose={handleAfterClose}
        closable={false}
        title={null}
        footer={null}
        width={600}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="text-refly-text-0 text-lg font-semibold leading-6">{title}</div>
            <Button type="text" icon={<Close size={24} />} onClick={handleModalClose} />
          </div>

          <div className="flex-grow min-h-0 overflow-y-auto">
            {!disableChangeVariableType && (
              <div className="flex items-center justify-between gap-2 mb-4">
                {variableTypeOptions.map((option) => (
                  <div
                    key={option.value}
                    className={cn(
                      'flex-1 h-9 box-border px-2 py-1 text-sm leading-5 flex items-center justify-center gap-1 rounded-lg bg-refly-bg-control-z1 border-[1px] border-solid border-refly-Card-Border hover:!text-refly-primary-default hover:!border-refly-primary-default cursor-pointer',
                      variableType === option.value
                        ? 'text-refly-primary-default border-refly-primary-default font-semibold'
                        : '',
                    )}
                    onClick={() => handleVariableTypeChange(option.value)}
                  >
                    {option.icon}
                    {option.label}
                  </div>
                ))}
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onValuesChange={handleFormValuesChange}
              initialValues={{
                required: true,
                isSingle: true,
                value: [{ type: 'text', text: '' }],
                currentOption: '',
                resourceTypes: RESOURCE_TYPE,
              }}
            >
              <Form.Item
                label={t('canvas.workflow.variables.name') || 'Variable Name'}
                name="name"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      // Check for duplicate names in workflowVariables array
                      const trimmedName = value.trim();
                      const duplicateVariable = workflowVariables?.find(
                        (variable) =>
                          variable.name === trimmedName &&
                          variable.variableId !== defaultValue?.variableId,
                      );

                      if (duplicateVariable) {
                        throw new Error(
                          t('canvas.workflow.variables.duplicateName') ||
                            'Variable name already exists. Please choose a different name.',
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  variant="filled"
                  placeholder={t('canvas.workflow.variables.inputPlaceholder') || 'Please enter'}
                  onBlur={() => handleInputBlur('name')}
                />
              </Form.Item>
              {renderForm()}
              <Form.Item name="required" valuePropName="checked">
                <Checkbox className="required-checkbox">
                  <span className="text-refly-text-0 text-sm font-semibold">
                    {t('canvas.workflow.variables.required')}
                  </span>
                </Checkbox>
              </Form.Item>
            </Form>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button className="w-[80px]" onClick={handleModalClose} disabled={submitting}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              className="w-[80px]"
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting || uploading}
            >
              {t('common.save') || 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);

CreateVariablesModal.displayName = 'CreateVariablesModal';
